import {
  Checklist as ChecklistIcon,
  Draw as DrawIcon,
} from "@mui/icons-material";
import clsx from "clsx";
import { FC, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useSetRecoilState } from "recoil";

import { MapLibreLassoSelect } from "@ryde/controls";
import {
  MAP_DATA_CONTROLS_ATTR,
  onActivateKeyDown,
  stationsMapBulkPanelOpenAtom,
  stationsMapSelectedIdsAtom,
} from "@ryde/shared";

import { getStationIdsInsideRing } from "./stations-map.utils";
import { StationsMapControlsStyled } from "./stations.map.styled";
import { useStationsMapSelectedStations } from "./use-stations-map-selected-stations";

/** Stations map control buttons (draw-select + bulk target) — mirrors the passengers map controls. */
export const StationsMapControls: FC = () => {
  const { t } = useTranslation();
  const setMapSelectedIds = useSetRecoilState(stationsMapSelectedIdsAtom);
  const { selectedStations } = useStationsMapSelectedStations();
  const [isDrawing, setIsDrawing] = useState(false);
  // The panel is rendered OUTSIDE the map (see StationsGridMap) so its inputs focus normally; we only
  // toggle its open state from here.
  const [isBulkPanelOpen, setIsBulkPanelOpen] = useRecoilState(
    stationsMapBulkPanelOpenAtom,
  );

  // Read latest stations inside the complete handler without re-arming the draw tool.
  const stationsRef = useRef(selectedStations);
  stationsRef.current = selectedStations;

  const toggleDraw = useEvent((): void => {
    setIsBulkPanelOpen(false);
    setIsDrawing(prev => !prev);
  });

  const toggleBulk = useEvent((): void => {
    setIsDrawing(false);
    setIsBulkPanelOpen(prev => !prev);
  });

  const handleLassoComplete = useEvent((ring: [number, number][]): void => {
    const insideIds = getStationIdsInsideRing(stationsRef.current, ring);

    if (insideIds.length) {
      setMapSelectedIds(prev => Array.from(new Set([...prev, ...insideIds])));
    }

    setIsDrawing(false);
  });

  const handleLassoCancel = useEvent((): void => setIsDrawing(false));

  const drawLabel = isDrawing
    ? t("station.mapCancelDrawing")
    : t("station.mapSelectByDrawing");
  const bulkLabel = t("station.mapBulkActionTarget");

  // A control is disabled when the other control is active, or when nothing is selected —
  // but the active control itself is always enabled so it can be toggled off.
  const hasSelectedStations = selectedStations.length > 0;
  const isDrawDisabled =
    !isDrawing && (isBulkPanelOpen || !hasSelectedStations);
  const isBulkDisabled =
    !isBulkPanelOpen && (isDrawing || !hasSelectedStations);

  return (
    <>
      <MapLibreLassoSelect
        isActive={isDrawing}
        onComplete={handleLassoComplete}
        onCancel={handleLassoCancel}
      />

      <div
        {...{ [MAP_DATA_CONTROLS_ATTR]: true }}
        onClick={e => e.stopPropagation()}
        role="presentation"
      >
        <StationsMapControlsStyled>
          <span
            onClick={isDrawDisabled ? undefined : toggleDraw}
            onKeyDown={
              isDrawDisabled ? undefined : onActivateKeyDown(toggleDraw)
            }
            role="button"
            tabIndex={isDrawDisabled ? -1 : 0}
            aria-label={drawLabel}
            aria-disabled={isDrawDisabled}
            title={drawLabel}
            className={clsx("mapControlBtn", {
              selected: isDrawing,
              disabled: isDrawDisabled,
            })}
          >
            <DrawIcon className="mapControlBtn-icon" />
          </span>

          <span
            onClick={isBulkDisabled ? undefined : toggleBulk}
            onKeyDown={
              isBulkDisabled ? undefined : onActivateKeyDown(toggleBulk)
            }
            role="button"
            tabIndex={isBulkDisabled ? -1 : 0}
            aria-label={bulkLabel}
            aria-disabled={isBulkDisabled}
            title={bulkLabel}
            className={clsx("mapControlBtn", {
              selected: isBulkPanelOpen,
              disabled: isBulkDisabled,
            })}
          >
            <ChecklistIcon className="mapControlBtn-icon" />
          </span>
        </StationsMapControlsStyled>
      </div>
    </>
  );
};
